import { flash, SubmitButton } from "@xola/ui-kit";
import { AxiosResponse } from "axios";
import { FormikProps } from "formik";
import { omit } from "lodash";
import React, { useRef, useState } from "react";
import { useMutation } from "react-query";
import { useHistory, useParams } from "react-router-dom";
import {
    Experience,
    ExperienceNotification,
    NotificationPreferenceType,
    ReviewDestination,
    ReviewPreference,
} from "types";
import { api } from "../../api/api";
import { ScrollToFieldError } from "../../helpers/validationHelper";
import { useInvalidate } from "../../hooks/useInvalidation";
import { useResource } from "../../hooks/useResource";
import { sanitizeExperiencePayload } from "../../models/Experience";
import { getStrippedHtml } from "../../utils/getStrippedHtml";
import { OverrideAlertBox } from "../Common/OverrideAlertBox";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { ContentLayout } from "../Layout/ContentLayout";
import { CustomReviewEmailTemplate } from "./CustomReviewEmailTemplate";
import { CustomReviewSmsTemplate } from "./CustomReviewSmsTemplate";
import { reviewPreferenceValidationSchema } from "./GlobalSettings/GlobalReviews";
import { getNotificationsWithDefaults } from "./GuestManagement/NotificationsSection";
import { ReviewForm } from "./ReviewForm";

export const ExperienceReviews = ({ reviewPreference }: { reviewPreference?: ReviewPreference }) => {
    const { id: experienceId } = useParams<{ id?: ID }>();
    const [shouldOverride, setShouldOverride] = useState(!!reviewPreference?.id);
    const invalidate = useInvalidate();
    const history = useHistory();
    const reviewPreferenceUrl = `/experiences/${experienceId}/preferences/review`;
    const computeInitialSendSMS = (review?: ReviewPreference) =>
        review?.destination === ReviewDestination.CUSTOM && Boolean(review?.reviewLink?.trim());
    const [sendSMS, setSendSMS] = useState(computeInitialSendSMS(reviewPreference));
    const formikRef = useRef<FormikProps<ReviewPreference>>(null);

    const { data: experience } = useResource<Experience>(["experiences", experienceId], null, {
        staleTime: 1000, // Cache for 1s so that its always re-fetched when the page loads
        keepPreviousData: true,
    });

    const { mutateAsync: deleteReviewPreference } = useMutation(async () => {
        return api.delete(reviewPreferenceUrl);
    });

    const handleOverrideSwitchChange = (value: boolean) => {
        setShouldOverride(value);
    };

    const { mutateAsync: saveReview } = useMutation(
        async (payload: ReviewPreference): Promise<AxiosResponse<ReviewPreference>> => {
            return payload?.id
                ? api.put<ReviewPreference>(reviewPreferenceUrl, payload)
                : api.post<ReviewPreference>(reviewPreferenceUrl, payload);
        },
    );

    const onSave = async (values: ReviewPreference) => {
        if (shouldOverride) {
            const payload = { ...values };
            // Only include reviewTemplate if destination is CUSTOM
            if (values.destination !== ReviewDestination.CUSTOM) {
                delete payload.reviewTemplate;
            }
            // Only include reviewLink if destination is GOOGLE or CUSTOM
            if (values.destination !== ReviewDestination.CUSTOM && values.destination !== ReviewDestination.GOOGLE) {
                delete payload.reviewLink;
            } else if (values.destination === ReviewDestination.CUSTOM) {
                // For CUSTOM: clear reviewLink if sendSMS is false, otherwise trim it
                payload.reviewLink = sendSMS ? payload.reviewLink?.trim() : "";
            } else {
                payload.reviewLink = payload.reviewLink?.trim();
            }
            const { data: result } = await saveReview(omit(payload, "isFormDirty"));

            // Only sync notification recipients when the sendReviewEmailToAllTravelers toggle has changed
            const existingNotifications = getNotificationsWithDefaults(experience?.notifications);
            const existingReviewNotification = existingNotifications.find(
                (n) => n.type === NotificationPreferenceType.REVIEW_REMINDER_TRAVELER,
            );
            const initialSendToAll = existingReviewNotification?.recipients === "travelers";
            const sendToAllChanged = values.sendReviewEmailToAllTravelers !== initialSendToAll;

            let updatedExperience = experience;
            if (experience && sendToAllChanged) {
                const updatedNotifications: ExperienceNotification[] = existingNotifications.map((n) =>
                    n.type === NotificationPreferenceType.REVIEW_REMINDER_TRAVELER
                        ? { ...n, recipients: values.sendReviewEmailToAllTravelers ? "travelers" : "organizer" }
                        : n,
                );
                await api.put(
                    `/experiences/${experienceId}`,
                    sanitizeExperiencePayload({ ...experience, notifications: updatedNotifications }),
                );
                updatedExperience = { ...experience, notifications: updatedNotifications };
            }

            formikRef.current?.setValues(getInitialValues(result, updatedExperience));
            setSendSMS(computeInitialSendSMS(result));
            invalidate("experiences", experienceId);
            flash.success("Successfully updated review preference");
        } else {
            await deleteReviewPreference();
            invalidate("experiences", experienceId);
        }
    };

    const handleGlobalReviewLink = () => {
        history.push("/products/globalSettings/reviews");
    };

    return (
        <ContentLayout isWide>
            <XolaFormik
                enableReinitialize
                initialValues={getInitialValues(reviewPreference, experience)}
                validationSchema={reviewPreferenceValidationSchema(sendSMS)}
                innerRef={formikRef}
                validateOnChange={false}
                validateOnBlur={false}
                onSubmit={onSave}
            >
                {({ setFieldValue, handleSubmit, handleReset, setValues }) => {
                    const handleResetClick = (handleReset: () => void) => () => {
                        handleReset();
                        setValues(getInitialValues(reviewPreference, experience));
                        setSendSMS(computeInitialSendSMS(reviewPreference));
                    };

                    return (
                        <>
                            <h2>Customer Reviews</h2>
                            <div className="mb-10 divide-y divide-gray-light">
                                <ContentLayout.Section
                                    title="Review Destination"
                                    description="Review emails are sent to customers one day after trip completion"
                                >
                                    <div className="max-w-[560px] space-y-6">
                                        <OverrideAlertBox
                                            shouldOverride={shouldOverride}
                                            switchText="Override global review settings"
                                            onChange={async (value) => {
                                                handleOverrideSwitchChange(value);
                                                setFieldValue("isFormDirty", true);
                                            }}
                                        >
                                            <span>
                                                This experience is using the{" "}
                                                <a
                                                    className="cursor-pointer font-bold text-primary-dark"
                                                    onClick={handleGlobalReviewLink}
                                                >
                                                    Global Review Settings
                                                </a>
                                                . You can set custom review preferences specific to this experience by
                                                overriding the global settings below.
                                            </span>
                                        </OverrideAlertBox>
                                        {shouldOverride && (
                                            <div className="mt-4">
                                                <ReviewForm experience={experience} />
                                            </div>
                                        )}
                                    </div>
                                </ContentLayout.Section>

                                {shouldOverride && (
                                    <>
                                        <CustomReviewEmailTemplate experience={experience} />
                                        <CustomReviewSmsTemplate
                                            experience={experience}
                                            sendSMS={sendSMS}
                                            setSendSMS={setSendSMS}
                                        />
                                    </>
                                )}
                            </div>
                            <ContentLayout.Footer preferenceSidebar>
                                <SubmitButton onClick={handleSubmit}>Save</SubmitButton>

                                <SecondaryButton type="reset" onClick={handleResetClick(handleReset)}>
                                    Cancel
                                </SecondaryButton>
                            </ContentLayout.Footer>

                            <ScrollToFieldError />
                        </>
                    );
                }}
            </XolaFormik>
        </ContentLayout>
    );
};

interface ReviewPreferenceForm extends ReviewPreference {
    isFormDirty?: boolean;
}

const getInitialValues = (reviewPreference?: ReviewPreference, experience?: Experience): ReviewPreferenceForm => {
    const defaultValues = {
        destination: ReviewDestination.OFF,
        sendReviewEmailToAllTravelers: false,
        enableSmartReview: false,
        smartReviewThreshold: 4,
    };

    const initialValues = { ...defaultValues, ...reviewPreference };

    // Derive sendReviewEmailToAllTravelers from experience notification recipients
    if (experience) {
        const notifications = getNotificationsWithDefaults(experience.notifications);
        const reviewNotification = notifications.find(
            (n) => n.type === NotificationPreferenceType.REVIEW_REMINDER_TRAVELER,
        );
        initialValues.sendReviewEmailToAllTravelers = reviewNotification?.recipients === "travelers";
    }

    if (initialValues.reviewTemplate) {
        initialValues.reviewTemplate = getStrippedHtml(initialValues.reviewTemplate) ?? "";
    }

    return { ...initialValues, isFormDirty: false };
};
